import React from 'react';
import { Alert, Box, Button, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Bot, Snapshot, SnapshotMeta, StatusColor } from './lib/types';
import { ActionRow, SortKey, actionRows, botBySlug, findAction, instructionById, kpis, unreadByBot } from './lib/selectors';
import { ageMinutes } from './lib/ict';
import { useBotOpsUrlState } from './hooks/useBotOpsUrlState';
import { AsOfBar, SourcesDialog } from './AsOfBar';
import { HeaderBar } from './HeaderBar';
import { SummaryStrip } from './SummaryStrip';
import { OrgHierarchy } from './OrgHierarchy';
import { GrantAsksPanel } from './GrantAsksPanel';
import { ActionCenter } from './ActionCenter';
import { DecisionQueue } from './DecisionQueue';
import { BotTable } from './BotTable';
import { BotDrawer } from './BotDrawer';
import { RecommendationDrawer } from './RecommendationDrawer';
import { BotOpsDataProvider, DataGate } from './BotOpsData';
import { DecisionControl } from './DecisionControl';
import { InboxPanel, QuestionPanel } from './InboxPanel';
import { SyncNow } from './SyncNow';
import { DigestStrip } from './DigestStrip';
import { BulkBar, isSelectable } from './BulkBar';
import { WhyDialog } from './WhyDialog';
import { usePrefs } from './hooks/usePrefs';
import { useSyncNow, useSyncPoll } from './hooks/useSync';
import { NoticeLine, PollErrorStrip, StaleStrip, WarningsStrip } from './states';

const STALE_AFTER_MIN = 120;

/** Scroll an element into view. jsdom has no scrollIntoView, so it is guarded. */
function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function Operations({ snapshot, meta, sync, onRefresh, pollError, hasPending, onApply }: {
  snapshot: Snapshot;
  meta: SnapshotMeta | null;
  sync: { lastSyncedAt: number | null; polling: boolean; failed: boolean };
  onRefresh: () => void;
  pollError: Parameters<typeof PollErrorStrip>[0]['error'] | null;
  hasPending: boolean;
  onApply: () => void;
}) {
  const [url, setUrl] = useBotOpsUrlState();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});
  const [toast, setToast] = React.useState<string | null>(null);
  const [highlightId, setHighlightId] = React.useState<string | null>(null);
  const [whyRow, setWhyRow] = React.useState<ActionRow | null>(null);
  const [picked, setPicked] = React.useState<Set<string>>(new Set());
  const { prefs } = usePrefs();

  const company = url.company;
  const names = snapshot.companies;
  const k = React.useMemo(() => kpis(snapshot, company), [snapshot, company]);
  const rows = React.useMemo(() => actionRows(snapshot, { company, bot: url.bot }), [snapshot, company, url.bot]);
  // The queue always ranks the whole company, whichever bot the Action Center is narrowed to.
  const companyRows = React.useMemo(() => (url.bot ? actionRows(snapshot, { company }) : rows), [snapshot, company, url.bot, rows]);
  const selectedBot = botBySlug(snapshot, url.bot) || null;
  const itemRow: ActionRow | null = url.action ? findAction(snapshot, url.action) : null;
  const botForDrawer: Bot | null = url.drawerOpen && !url.action ? selectedBot : null;
  const ageMin = ageMinutes(snapshot.generated_ict);
  const canWrite = Boolean(meta && meta.can_write);
  const unread = React.useMemo(() => (prefs && prefs.notify.unread_badges === false ? {} : unreadByBot(snapshot)), [snapshot, prefs]);
  // Only pending, decidable rows of the company on screen can be selected; anything decided or removed since drops out.
  const selectable = React.useMemo(() => new Set(rows.filter((r) => isSelectable(r.action)).map((r) => r.action.id)), [rows]);
  const selectedIds = React.useMemo(() => Array.from(picked).filter((id) => selectable.has(id)), [picked, selectable]);
  const togglePick = (id: string) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  React.useEffect(() => { setPicked(new Set()); }, [company]);
  const inboxUnread = selectedBot ? unread[selectedBot.slug] || 0 : 0;

  useSyncPoll({ refresh: onRefresh, threadOpen: Boolean(url.drawerOpen && url.tab === 'inbox') });
  const syncNow = useSyncNow({ enabled: canWrite, generatedIct: snapshot.generated_ict, refresh: onRefresh });

  // A link to a bot or item that is not in this snapshot: say so, and drop it.
  React.useEffect(() => {
    if (url.action && !itemRow) {
      setToast('That item is not in the latest snapshot.');
      setUrl({ action: null });
    } else if (url.bot && !selectedBot) {
      setToast('That bot is not in the latest snapshot.');
      setUrl({ bot: null, tab: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url.action, url.bot, itemRow, selectedBot]);

  // ?instruction=<id> (a pasted link, or "Instruct instead" just sent) opens that bot's Inbox on the thread.
  React.useEffect(() => {
    if (!url.instruction || url.drawerOpen) return;
    const inst = instructionById(snapshot, url.instruction);
    if (inst) setUrl({ bot: inst.target_bot_slug, tab: 'inbox' });
    else if (!snapshot.instructions || !snapshot.instructions.length || !inst) { setToast('That thread is not in the latest data.'); setUrl({ instruction: null }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url.instruction, url.drawerOpen]);

  const openInstruction = (id: string) => {
    const inst = instructionById(snapshot, id);
    setUrl({ bot: inst ? inst.target_bot_slug : url.bot, tab: 'inbox', action: null, instruction: id }, { push: true });
  };

  // ?ga= opens that Grant-asks row.
  const toggleGa = (id: string) => setUrl({ ga: url.ga === id ? null : id });

  // Clicking a bot anywhere opens its own page; the drawer stays as the quick preview (table "preview" link, ?bot=&tab=).
  const botPath = (slug: string, tab?: string) => `/dashboard/bot-ops/bots/${encodeURIComponent(slug)}${tab ? `?tab=${tab}` : ''}`;
  const goBot = (b: Bot, tab?: string) => navigate(botPath(b.slug, tab));
  const openBotDrawer = (b: Bot) => {
    // Clicking the selected node again, with its drawer open, clears the selection.
    if (url.bot === b.slug && url.drawerOpen && !url.action) setUrl({ bot: null, tab: null, action: null });
    else setUrl({ bot: b.slug, tab: 'inbox', action: null }, { push: true });
  };
  const openAction = (id: string) => setUrl({ action: id }, { push: true });
  const closeDrawer = () => setUrl({ bot: null, tab: null, action: null, instruction: null });
  const showInActionCenter = (b: Bot) => {
    setUrl({ bot: b.slug, tab: null, action: null });
    window.setTimeout(() => scrollToId('bo-actions'), 0);
  };
  const setStatus = (c: StatusColor | null) => setUrl({ status: c });
  const sortBy = (key: SortKey) => {
    if (url.sort === key) setUrl({ sort: key, dir: url.dir === 'asc' ? 'desc' : 'asc' });
    else setUrl({ sort: key, dir: 'asc' });
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast('Link copied');
    } catch {
      setToast('Could not copy. The address bar has the link.');
    }
  };
  const goGa = (id: string) => {
    setUrl({ ga: id });
    window.setTimeout(() => scrollToId('bo-ga'), 0);
  };
  const firstOpenGa = snapshot.ledger.find((r) => /^GA-/.test(r.id) && /^OPEN/i.test(r.status) && !r.struck);

  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto', p: { xs: 1.5, md: 3 } }}>
      <AsOfBar snapshot={snapshot} readOnly={!canWrite} onOpen={() => setUrl({ panel: 'sources' })} />
      {ageMin != null && ageMin > STALE_AFTER_MIN && <StaleStrip minutes={ageMin} />}
      <WarningsStrip warnings={snapshot.warnings || []} />
      {pollError && <PollErrorStrip error={pollError} onRetry={onRefresh} />}
      {hasPending && (
        <NoticeLine tone="info" testId="new-data" action={<Button color="inherit" size="small" onClick={onApply} sx={{ minWidth: 0, py: 0, px: 0.75, fontSize: 12.5, fontWeight: 700, textTransform: 'none' }}>Apply</Button>}>
          New data · held while you are typing. Apply it when you are ready.
        </NoticeLine>
      )}

      <DigestStrip snapshot={snapshot} company={company} canWrite={canWrite} />

      <HeaderBar
        snapshot={snapshot}
        company={company}
        onCompany={(c) => setUrl({ company: c })}
        statusFilter={url.status}
        onStatus={setStatus}
        sync={sync}
        syncControl={canWrite ? <SyncNow state={syncNow} onSync={syncNow.sync} /> : undefined}
      />

      <SummaryStrip
        kpis={k}
        company={company}
        companyName={names[company].name}
        statusFilter={url.status}
        onStatus={(c) => { setStatus(c); window.setTimeout(() => scrollToId('bo-table'), 0); }}
        onOpenItems={() => { setUrl({ decision: 'all' }); scrollToId('bo-actions'); }}
        onAwaiting={() => scrollToId('bo-decide')}
        onGa={() => { if (firstOpenGa) goGa(firstOpenGa.id); else scrollToId('bo-ga'); }}
      />

      <DecisionQueue
        rows={companyRows}
        company={company}
        companyName={names[company].name}
        renderDecision={(r) => <DecisionControl row={r} snapshot={snapshot} onOpenInstruction={openInstruction} />}
        onOpenAction={openAction}
        onOpenBot={(slug) => navigate(botPath(slug))}
        onWhy={setWhyRow}
        onSeeAll={() => { setUrl({ decision: 'pending', bot: null, tab: null, team: null, area: null, bucket: null, impl: null }); window.setTimeout(() => scrollToId('bo-actions'), 0); }}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) 380px' }, gap: 2, mb: 2, alignItems: 'start' }}>
        <OrgHierarchy
          snapshot={snapshot}
          company={company}
          selected={url.bot}
          collapsed={collapsed}
          onToggleCluster={(n) => setCollapsed((c) => ({ ...c, [n]: !c[n] }))}
          onSelect={(b) => goBot(b)}
          unread={unread}
        />
        <Box id="bo-ga">
          <GrantAsksPanel snapshot={snapshot} expandedId={url.ga} onToggle={toggleGa} onOpenAction={openAction} />
        </Box>
      </Box>

      <ActionCenter
        snapshot={snapshot}
        company={company}
        rows={rows}
        allRowsForCounts={rows}
        decision={url.decision}
        onDecisionFilter={(d) => setUrl({ decision: d })}
        team={url.team}
        area={url.area}
        onTeam={(e) => setUrl({ team: e })}
        onArea={(a) => setUrl({ area: a })}
        bucket={url.bucket}
        impl={url.impl}
        onBucket={(b) => setUrl({ bucket: b })}
        onImpl={(i) => setUrl({ impl: i })}
        selectedBot={selectedBot}
        onClearBot={() => setUrl({ bot: null, tab: null, action: null })}
        onInstructBot={(b) => goBot(b, 'inbox')}
        onOpenBotDetail={(b) => goBot(b)}
        highlightId={highlightId}
        onOpenAction={openAction}
        onOpenBot={(b) => goBot(b)}
        onGa={goGa}
        unread={unread}
        renderDecision={(p) => <DecisionControl row={p.row} snapshot={snapshot} onOpenInstruction={openInstruction} />}
        onWhy={setWhyRow}
        selected={picked}
        onToggleSelect={canWrite ? togglePick : undefined}
        bulkBar={canWrite ? <BulkBar selected={selectedIds} onClear={() => setPicked(new Set())} refresh={onRefresh} /> : undefined}
      />

      <BotTable
        snapshot={snapshot}
        company={company}
        status={url.status}
        exec={url.exec}
        sort={url.sort}
        dir={url.dir}
        onSort={sortBy}
        onStatus={setStatus}
        onExec={(e) => setUrl({ exec: e })}
        onOpenBot={(b) => goBot(b)}
        onPreviewBot={openBotDrawer}
        onOpenAction={openAction}
        onBlockers={(b) => goBot(b)}
        unread={unread}
      />

      <Box component="footer" sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center', py: 2 }}>
        Compiled by Grok Bot from the team files. Green = active in the last {snapshot.active_window_hours} h · yellow = awaiting Grant or an exec alternative · red = idle. Blocker text is always shown. Times ICT.
      </Box>

      <BotDrawer
        snapshot={snapshot}
        company={company}
        bot={botForDrawer}
        tab={url.tab}
        onTab={(t) => setUrl({ tab: t })}
        onClose={closeDrawer}
        onShowInActionCenter={showInActionCenter}
        onOpenAction={openAction}
        onOpenBot={(slug) => navigate(botPath(slug))}
        onOpenFullPage={(b) => goBot(b)}
        inboxUnread={inboxUnread}
        renderInbox={(b) => <InboxPanel snapshot={snapshot} bot={b} openId={url.instruction} />}
      />

      <RecommendationDrawer
        row={itemRow}
        company={company}
        onClose={closeDrawer}
        onOpenBot={(slug) => navigate(botPath(slug))}
        onShowInActionCenter={(r) => { setHighlightId(r.action.id); setUrl({ bot: r.bot.slug, tab: null, action: null }); window.setTimeout(() => scrollToId('bo-actions'), 0); }}
        onCopyLink={copyLink}
        onGa={(id) => { closeDrawer(); goGa(id); }}
        renderDecision={(r) => <DecisionControl row={r} snapshot={snapshot} onOpenInstruction={openInstruction} layout="drawer" />}
        renderQuestion={(r) => <QuestionPanel snapshot={snapshot} row={r} openId={url.instruction} />}
        onWhy={setWhyRow}
      />

      <WhyDialog row={whyRow} snapshot={snapshot} onClose={() => setWhyRow(null)} onGa={(id) => { closeDrawer(); goGa(id); }} />

      <SourcesDialog open={url.panel === 'sources'} onClose={() => setUrl({ panel: null })} snapshot={snapshot} meta={meta} />

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} variant="filled">{toast}</Alert>
      </Snackbar>
    </Box>
  );
}

/** The Operations view, for the nested route (the shared provider is above it). */
export function OperationsView() {
  return (
    <DataGate>
      {({ snapshot, meta, data, error }) => (
        <Operations
          snapshot={snapshot}
          meta={meta}
          sync={{ lastSyncedAt: data.lastSyncedAt, polling: data.polling, failed: Boolean(error) }}
          onRefresh={data.refresh}
          pollError={error}
          hasPending={data.hasPending}
          onApply={data.applyPending}
        />
      )}
    </DataGate>
  );
}

/** Self-contained page (its own provider), used where it is rendered outside the nested routes. */
export default function BotOpsPage() {
  return (
    <BotOpsDataProvider>
      <OperationsView />
    </BotOpsDataProvider>
  );
}
